import { useEffect, useState, useCallback } from 'react';
import { Button, ButtonType, ButtonSize, IconName } from '@hills-design-system/components';
import { useOrdersState, useOrdersActions } from '../../../hooks/useOrders';
import { Order, OrderStatus } from '../../../atoms/ordersAtom'; // Import OrderStatus enum
import { Card } from '../../../components/Card/Card'; // Import the Card component
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
// TEMPORARY: Commented out unused imports for temporary track order change
// import { fetchOrderTracking } from '../../../services/api';
// import { TrackingProvider, generateUPSTrackingUrl } from '../../../types/tracking';
import HroInformationModal from '../../../components/Modal/HroInformationModal';
import { analytics } from '../../../services/analytics';
import { ShipToFilter } from './ShipToFilter';
import { useShipToLocationsState } from '../../../hooks/useShipToLocations';
import { useCutoffActions, useCutoffState } from '../../../hooks/useCutoffDate';
import { format, parseISO } from 'date-fns';
import { Tooltip } from '../../../components/Tooltip';
import { OrderSectionLayout } from './OrderSectionLayout';

const getStatusColor = (status: OrderStatus): string => {
  switch (status) {
    case OrderStatus.InProgress:
      return 'text-black-950';
    case OrderStatus.Complete:
      return 'text-grass-700';
    case OrderStatus.Cancelled:
      return 'text-red-700';
    case OrderStatus.Rejected:
      return 'text-red-700';
    default:
      return 'text-black-950';
  }
};

// Helper function to convert enum to display text
const getStatusDisplayText = (
  status: OrderStatus,
  t: (key: TranslationKeyEnum, ...args: string[]) => string
): string => {
  switch (status) {
    case OrderStatus.InProgress:
      return t(TranslationKeyEnum.InProgress);
    case OrderStatus.Complete:
      return t(TranslationKeyEnum.Complete);
    case OrderStatus.Open:
      return t(TranslationKeyEnum.Open);
    case OrderStatus.Cancelled:
      return t(TranslationKeyEnum.Cancelled);
    case OrderStatus.Rejected:
      return t(TranslationKeyEnum.Rejected);
    default:
      return ''; // Or handle unexpected status
  }
};

export const OrderSection = () => {
  const ordersData = useOrdersState();
  const ordersActions = useOrdersActions();
  const cutoffActions = useCutoffActions();
  const cutoffData = useCutoffState();
  const shipToLocations = useShipToLocationsState();

  const { getOrders } = ordersActions ?? {};
  const { getCutoff, setCutoff } = cutoffActions ?? {};
  const { t } = useTranslation();

  // State for managing expanded view
  const [isExpanded, setIsExpanded] = useState(false);

  // Show HRO Information Modal
  const [openHroModal, setOpenHroModal] = useState({ visible: false, fullUrl: '' });

  // State for selected ship-to location
  const [selectedShipTo, setSelectedShipTo] = useState<string | null>(null);

  // TEMPORARY: Commented out loading state for temporary track order change
  // const [loadingTrackingOrder, setLoadingTrackingOrder] = useState<string | null>(null);

  // Handle ship-to filter change
  const handleShipToChange = useCallback((shipTo: string | null) => {
    console.debug('[OrderSection] Ship-to filter changed:', shipTo);
    setSelectedShipTo(shipTo);
  }, []);

  // Fetch orders when ship-to filter changes
  useEffect(() => {
    if (getOrders && typeof getOrders === 'function') {
      getOrders(selectedShipTo ?? undefined);
    }
  }, [getOrders, selectedShipTo]);

  // Fetch cutoff date when ship-to filter changes (to update the cutoff date displayed on the card)
  useEffect(() => {
    if (getCutoff && selectedShipTo && typeof getCutoff === 'function') {
      getCutoff(selectedShipTo);
    } else if (!selectedShipTo) {
      // Set cutoff to null when no ship-to is selected
      setCutoff(null);
    }
  }, [getCutoff, setCutoff, selectedShipTo]);

  // Get all past orders (both in-progress and completed)
  const pastOrders = ordersData?.orders ?? [];

  // Determine which orders to display based on expansion state
  // Collapsed: show only 1 order (the most recent), Expanded: show all with stacking effect
  const hasMoreThanOneOrder = pastOrders.length > 1;

  // Show only 1 past order when collapsed, all when expanded
  const maxPastOrdersToShow = isExpanded ? pastOrders.length : 1;
  const displayedPastOrders = pastOrders.slice(0, maxPastOrdersToShow);

  // Find the first in-progress order with an estimated arrival date
  // const relevantInProgressOrder = inProgressOrders.find((order) => order.estimatedArrivalDate);

  // Helper function to navigate to HRO pages
  const navigateToHRO = (path: string) => {
    const hroBaseUrl = import.meta.env.VITE_HRO_BASE_URL ?? 'https://dev.hillsretailorder.com';
    const fullUrl = `${hroBaseUrl}${path}`;
    const hasSeenOrderIntegrationModal = localStorage.getItem('hasSeenOrderIntegrationModal');
    if (!hasSeenOrderIntegrationModal) {
      setOpenHroModal({ visible: true, fullUrl });
      return;
    }
    console.log(`[OrderSection] Navigating to HRO: ${fullUrl}`);
    window.open(fullUrl, '_blank');
  };

  // Helper function to handle order button click
  const handleOrderClick = async (order: Order) => {
    const isComplete = order.status === OrderStatus.Complete;

    console.log(`[OrderSection] Button clicked for order ${order.id}`, {
      orderId: order.id,
      status: order.status,
      isComplete,
      trackingNumber: order.trackingNumber,
      trackingProvider: order.trackingProvider,
      hasTracking: !!order.trackingNumber,
    });

    if (isComplete) {
      // For completed orders, view order functionality
      console.log(`[OrderSection] View order clicked: ${order.id}`);
      analytics.navigate(
        `/fsl/order-history/order/${order.id}`,
        t(TranslationKeyEnum.ViewOrder),
        'orders',
        'order_complete'
      );
      navigateToHRO(`/fsl/order-history/order/${order.id}`);
      return;
    }

    // TEMPORARY: For in-progress orders, use same link as completed orders (open in new tab)
    // TODO: Restore original tracking logic after temporary period
    console.log(`[OrderSection] Track order clicked (temporary new tab behavior): ${order.id}`);
    analytics.navigate(
      `/fsl/order-history/order/${order.id}`,
      t(TranslationKeyEnum.ViewOrder),
      'orders',
      'order_in_progress'
    );
    navigateToHRO(`/fsl/order-history/order/${order.id}`);

    /* ORIGINAL TRACKING LOGIC - COMMENTED OUT FOR TEMPORARY CHANGE
    // For in-progress orders, fetch tracking URL on-demand
    try {
      // Set loading state
      setLoadingTrackingOrder(order.id);

      console.log(`[OrderSection] Fetching tracking information for order ${order.id}`);
      const trackingResponse = await fetchOrderTracking(order.id);

      // Check if tracking is unavailable
      if (trackingResponse.trackingUnavailable) {
        console.warn(
          `[OrderSection] Tracking unavailable for order ${order.id}:`,
          trackingResponse.message
        );
        return;
      }

      if (trackingResponse.trackingNumber && trackingResponse.provider) {
        console.log(`[OrderSection] Tracking info found for order ${order.id}:`, trackingResponse);

        // Handle UPS directly
        if (trackingResponse.provider === TrackingProvider.UPS) {
          const upsUrl = generateUPSTrackingUrl(trackingResponse.trackingNumber);
          window.open(upsUrl, '_blank');
        } else {
          // For Ryder/Telogis, tracking should now be disabled, but handle legacy case
          console.warn(
            `[OrderSection] Tracking disabled for provider ${trackingResponse.provider}`
          );
        }
      } else {
        console.warn(`[OrderSection] No tracking information available for order ${order.id}`);
      }
    } catch (error) {
      console.error(`[OrderSection] Failed to fetch tracking for order ${order.id}:`, error);
    } finally {
      setLoadingTrackingOrder(null);
    }
    */
  };

  // Helper function to render an order card
  const renderOrderCard = (order: Order) => {
    const statusText = getStatusDisplayText(order.status, t);
    const statusColor = getStatusColor(order.status);

    // TEMPORARY: Simplified button state for temporary track order change
    // For completed orders: always enabled (view order)
    // For in-progress orders: always enabled (temporary new tab behavior)
    const isButtonLoading = false; // No loading state in temporary implementation
    const isButtonEnabled = true; // Always enabled for both completed and in-progress orders

    return (
      <Card
        key={order.id}
        padding="large"
        radius="large" // Assuming rounded-400 maps to large radius
        customBackground="bg-gray-50" // Using custom background for specific gray
        className="flex justify-between items-center h-[72px]"
      >
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            <span className={`font-bold text-detail-90 ${statusColor} leading-detail`}>
              {statusText}
            </span>
            <span className="text-detail-90 text-black-200 leading-detail">•</span>
            <span className="text-detail-90 font-medium text-black-950 leading-detail">
              {t(TranslationKeyEnum.OrderNumber, order.id)}
            </span>
          </div>
          <span className="text-detail-90 font-medium text-black-950 leading-detail">
            {order.shiptoName}
          </span>
        </div>
        <Button
          label={isButtonLoading ? t(TranslationKeyEnum.Loading) : t(TranslationKeyEnum.ViewOrder)}
          data-id="cta"
          type={ButtonType.Tertiary}
          size={ButtonSize.Small}
          icon={isButtonLoading ? undefined : IconName.ExternalLink}
          onClick={() => handleOrderClick(order)}
          disabled={!isButtonEnabled}
          className="hover:bg-transparent"
        />
      </Card>
    );
  };

  // Helper function to render past orders with visual stacking effect (like Figma design)
  const renderStackedPastOrders = (orders: Order[], showOnlyFirst = false) => {
    if (orders.length === 0) return null;

    // When collapsed, show only the first order but with stacking visual effect
    const ordersToRender = showOnlyFirst ? [orders[0]] : orders;

    return (
      <div className="relative">
        {/* Render visual stacking effect - background cards */}
        {showOnlyFirst && orders.length > 1 && (
          <>
            {/* Bottom background card */}
            <div
              className="absolute top-8 left-4 right-4 h-[72px] bg-gray-200 rounded-2xl shadow-sm"
              style={{
                zIndex: 1,
              }}
            />
            {/* Middle background card */}
            <div
              className="absolute top-4 left-2 right-2 h-[72px] bg-gray-300 rounded-2xl shadow-sm"
              style={{
                zIndex: 2,
              }}
            />
          </>
        )}

        {/* Render actual order cards */}
        {ordersToRender.map((order, index) => {
          const zIndex = showOnlyFirst ? 10 : orders.length - index + 10; // Ensure top card is always on top

          return (
            <div
              key={order.id}
              className="relative"
              style={{
                zIndex,
              }}
            >
              {renderOrderCard(order)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <OrderSectionLayout
      sidebar={
        <>
          <ShipToFilter onShipToChange={handleShipToChange} />
          <Button
            label={t(TranslationKeyEnum.ViewAllOrders)}
            type={ButtonType.Tertiary}
            size={ButtonSize.Small}
            icon={IconName.ExternalLink}
            align="left"
            data-id="cta"
            className={'hover:bg-transparent w-fit'}
            onClick={() => {
              console.log(`[OrderSection] View all orders clicked`);
              const locationCount = shipToLocations?.locations?.length ?? 0;
              const orderHistoryPath =
                locationCount > 0
                  ? `/fsl/order-history?shipTo=${encodeURIComponent(selectedShipTo ?? 'all')}`
                  : '/fsl/order-history';
              analytics.navigate(
                orderHistoryPath,
                t(TranslationKeyEnum.ViewAllOrders),
                'mainnav',
                null
              );
              navigateToHRO(orderHistoryPath);
            }}
          />
        </>
      }
    >
          {/* Card 1 - Start order (with formatted date) */}
          <Card
            padding="large"
            radius="large" // Assuming rounded-400 maps to large radius
            customBackground="bg-primary" // Use bg-primary from config
            height="h-[280px]"
            className="flex flex-col lg:flex-row justify-between lg:items-end border-none overflow-visible" // Keep flex/layout styles
          >
            <div className="flex flex-col justify-between h-full">
              <h3 className="text-heading-200 font-bold text-white mb-[10px] leading-heading tracking-[-3%]">
                {t(TranslationKeyEnum.StartYourNextOrder)}
              </h3>
              {cutoffData?.cutoffDate && ( // Check if date string is not empty
                <div className="flex items-center gap-2 text-white">
                  <Tooltip
                    align="top"
                    size={16}
                    textClasses="text-white"
                    icon={IconName.InfoFilled}
                    content={
                      <div className="w-[360px] rounded-200 bg-white text-black font-normal shadow-md p-2">
                        <p> {t(TranslationKeyEnum.OrderCutOffDateTooltip)} </p>
                      </div>
                    }
                  />

                  <span className="text-detail-90 font-medium leading-detail text-white">
                    {t(
                      TranslationKeyEnum.OrderCutOffDate,
                      format(parseISO(cutoffData.cutoffDate), 'EEEE h a')
                    )}
                  </span>
                </div>
              )}
            </div>
            <div className="flex lg:flex-col gap-2">
              <Button
                label={t(TranslationKeyEnum.StartNewOrder)}
                type={ButtonType.Secondary}
                size={ButtonSize.Small}
                icon={IconName.ExternalLink}
                inverse
                fullWidth
                data-id="cta"
                onClick={() => {
                  analytics.ctaClickWithUrl(
                    t(TranslationKeyEnum.StartNewOrder),
                    '/fsl/shelter-products'
                  );
                  navigateToHRO('/fsl/shelter-products');
                }}
              />
              <Button
                label={t(TranslationKeyEnum.StartWithPastOrder)}
                type={ButtonType.Primary}
                size={ButtonSize.Small}
                icon={IconName.ExternalLink}
                inverse
                fullWidth
                data-id="cta"
                onClick={() => {
                  analytics.ctaClickWithUrl(
                    t(TranslationKeyEnum.StartWithPastOrder),
                    '/fsl/latest-order'
                  );
                  navigateToHRO('/fsl/latest-order');
                }}
              />
            </div>
          </Card>

          {/* Past Orders Section */}
          {ordersData === null ? (
            // Loading state
            <Card
              padding="large"
              radius="large"
              customBackground="bg-gray-50" // Use bg-gray-50 from config
              height="h-[72px]"
              className="flex justify-center items-center"
            >
              <span className="text-detail-90 font-medium text-black-600 leading-detail">
                {t(TranslationKeyEnum.LoadingOrders)}
              </span>
            </Card>
          ) : pastOrders.length === 0 ? (
            // No past orders
            <Card
              padding="large"
              radius="large"
              customBackground="bg-gray-50" // Use bg-gray-50 from config
              height="h-[72px]"
              className="flex justify-center items-center"
            >
              <span className="text-detail-90 font-medium text-black-600 leading-detail">
                {t(TranslationKeyEnum.NoOrdersInProgress)}
              </span>
            </Card>
          ) : (
            <div className={`transition-all duration-300 ease-in-out space-y-2`}>
              {/* Past orders - stacking effect when collapsed, normal when expanded */}
              {isExpanded ? (
                // When expanded, show all orders normally (as separate cards)
                <div className="space-y-2">
                  {displayedPastOrders.map((order) => (
                    <div key={order.id}>{renderOrderCard(order)}</div>
                  ))}
                </div>
              ) : hasMoreThanOneOrder ? (
                // When collapsed with multiple orders, use stacking effect to show there are more
                renderStackedPastOrders(pastOrders, true) // Pass true to show only first with stack effect
              ) : (
                // When collapsed with only one order, show normally
                <div className="space-y-2">
                  {displayedPastOrders.map((order) => (
                    <div key={order.id}>{renderOrderCard(order)}</div>
                  ))}
                </div>
              )}

              {/* Show More Button */}
              {hasMoreThanOneOrder && (
                <div className={`flex justify-center ${isExpanded ? 'pt-4' : 'pt-8'}`}>
                  <Button
                    label={
                      isExpanded ? t(TranslationKeyEnum.ShowLess) : t(TranslationKeyEnum.ShowMore)
                    }
                    type={ButtonType.Tertiary}
                    size={ButtonSize.Small}
                    icon={isExpanded ? IconName.ChevronUp : IconName.ChevronDown}
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="hover:bg-transparent"
                  />
                </div>
              )}
            </div>
          )}
      {/* HRO Information Modal */}
      {openHroModal.visible && (
        <HroInformationModal
          isOpen={openHroModal.visible}
          onClose={() => {
            setOpenHroModal({ visible: false, fullUrl: '' });
            localStorage.setItem('hasSeenOrderIntegrationModal', 'true');
            window.open(openHroModal.fullUrl, '_blank');
          }}
          title={t(TranslationKeyEnum.HroInformationModalTitle)}
          description={t(TranslationKeyEnum.HroInformationModalDescription)}
          buttonLabel={t(TranslationKeyEnum.HroInformationModalButtonLabel)}
          onClick={() => {
            setOpenHroModal({ visible: false, fullUrl: '' });
            localStorage.setItem('hasSeenOrderIntegrationModal', 'true');
            console.log(`[OrderSection] Navigating to HRO: ${openHroModal.fullUrl}`);
            window.open(openHroModal.fullUrl, '_blank');
          }}
        />
      )}
    </OrderSectionLayout>
  );
};
